import { Link } from 'react-router-dom';

/**
 * "Our story" — BestAcco's brand/about page. Original editorial copy in the
 * storefront's Warm Editorial voice. Intentionally avoids inventing specific
 * facts (dates, founders, press) that haven't been provided.
 */
export default function OurStoryPage() {
  return (
    <div className="bg-cream text-ink">
      {/* Hero */}
      <section className="mx-auto max-w-4xl px-6 py-20 text-center sm:py-28">
        <p className="font-semibold uppercase tracking-[0.2em] text-clay">
          Our story
        </p>
        <h1 className="mt-4 font-serif text-4xl font-semibold leading-tight sm:text-5xl">
          Stay somewhere with a story.
        </h1>
        <p className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-ink/70">
          BestAcco is a curated directory of distinctive, independently run
          hotels — the kind of places with a point of view, a sense of where they
          are, and a reason to remember them. We help you find them, and book
          them, without the noise.
        </p>
      </section>

      {/* Body */}
      <section className="mx-auto max-w-3xl px-6 pb-24 text-[17px] leading-relaxed text-ink/80 [&_h2]:mt-14 [&_h2]:font-serif [&_h2]:text-2xl [&_h2]:font-semibold [&_h2]:text-ink [&_p]:mt-4">
        <h2>Why we started</h2>
        <p>
          Travel has never had more choice and never felt more the same. Endless
          listings, identical rooms, prices that change while you blink. Somewhere
          in all of it, the places with real character — the small hotels, the
          restored buildings, the rooms shaped by the people who run them — got
          buried under everything else.
        </p>
        <p>
          BestAcco exists to bring those places back to the surface. We are
          building a home for stays worth choosing on purpose: hand-picked,
          honestly described, and easy to book.
        </p>

        <h2>What we believe</h2>
        <p>
          <strong>Character over volume.</strong> We would rather show you a
          short, considered list than an infinite scroll. Every Property on
          BestAcco earns its place.
        </p>
        <p>
          <strong>Independents deserve a fair stage.</strong> The hotels we
          feature are independently owned and operated. We give them a beautiful,
          direct way to reach guests — without losing their identity to a
          template.
        </p>
        <p>
          <strong>Booking should feel calm.</strong> Clear photos, clear prices,
          clear terms. You should know what you are paying, what is included, and
          what happens if your plans change — before you book, not after.
        </p>

        <h2>How BestAcco works</h2>
        <p>
          Browse our collection or search by where and when you want to go. Open a
          Property to see its rooms, rates, and the things that make it itself.
          When you find your stay, book it in a few steps — paying a deposit or at
          the Property, depending on the rate — and you are set. Each hotel is
          independently run and looks after your stay directly; we make the
          discovery and the booking simple, and we are here if you need us.
        </p>

        <h2>For hoteliers</h2>
        <p>
          If you run a hotel with a story to tell, we would love to hear from you.
          BestAcco gives independent properties a polished storefront, a direct
          booking channel, and distribution to the wider travel ecosystem —
          without the sameness. You keep your voice; we handle the plumbing.
        </p>

        <div className="mt-12 flex flex-wrap gap-4">
          <Link
            to="/search"
            className="rounded-md bg-ink px-6 py-3 font-semibold text-cream transition hover:bg-ink/90"
          >
            Browse hotels
          </Link>
          <Link
            to="/list-your-property"
            className="rounded-md border border-ink/20 px-6 py-3 font-semibold text-ink transition hover:border-clay hover:text-clay"
          >
            List your hotel
          </Link>
        </div>
      </section>
    </div>
  );
}
